import { Spinner2 } from 'components/Spinner2';
import React, { useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  IDealerConfigProps,
  useGetDealerConfigResultMutation,
  useGetOneDealerQuery,
  useSaveDraftConfigMutation,
  useUpdateDealerProdConfigurationMutation,
  useVerifyConfigResultComponentsMutation,
} from 'thunks/RTKQuery/dealer';

import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatJson } from 'utils/common';
import { Confirmation } from 'components/Modal';
import { confirmationSlice, toastSlice } from 'slices';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { CONFIGURATION_TYPE } from 'constants/constants';
import { ConfigComponent } from './ConfigComponent';

const initialConfig: IDealerConfigProps = {
  configurationType: CONFIGURATION_TYPE.NONE,
  draftConfiguration: '{}',
  draftTicketId: '',
  prodConfiguration: '{}',
  isInitialCall: true,
  isTestMode: true,
};

export const DealerConfigResult: React.FC = () => {
  const loggedInDealerId = useAppSelector(
    (state) => state.auth.data?.dealer?.id
  );

  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const [dealerConfig, setDealerConfig] = React.useState<IDealerConfigProps>(
    initialConfig
  );

  const [resultJson, setResultJson] = React.useState<any>(null);

  const [
    getConfigResult,
    { isError, error, isLoading, status: getConfigResultStatus, data },
  ] = useGetDealerConfigResultMutation();

  const [
    verifyComponentsExistence,
    {
      isError: isVerifyComponentsExistenceError,
      isLoading: isVerifyingComponentsExistence,
      error: verifyComponentsExistenceError,
      status: verifyComponentsExistenceStatus,
      data: verifyComponentsExistenceData,
    },
  ] = useVerifyConfigResultComponentsMutation();

  const [
    saveDraftConfig,
    {
      isLoading: isSavingDraftConfig,
      isError: isSavingDraftConfigError,
      error: saveDraftConfigError,
      status: saveDraftConfigStatus,
    },
  ] = useSaveDraftConfigMutation();

  const { data: dealerData } = useGetOneDealerQuery(loggedInDealerId || '', {
    skip: !loggedInDealerId,
    // Avoid unnecessary refetches
    refetchOnMountOrArgChange: false,
  });

  React.useEffect(() => {
    const configuration = dealerData?.configuration;
    if (configuration) {
      setDealerConfig({
        configurationType:
          configuration.configurationType || CONFIGURATION_TYPE.NONE,
        draftConfiguration: formatJson(
          configuration.draftConfiguration || '{}'
        ),
        prodConfiguration: formatJson(configuration.prodConfiguration || '{}'),
        draftTicketId: configuration.draftTicketId || '',
        isInitialCall: configuration.isInitialCall ?? true,
        isTestMode: configuration.isTestMode ?? true,
      });
    }
  }, [dealerData]);

  React.useEffect(() => {
    if (verifyComponentsExistenceStatus === QueryStatus.fulfilled) {
      setResultJson(verifyComponentsExistenceData);
    }

    if (verifyComponentsExistenceStatus === QueryStatus.rejected) {
      setResultJson(
        `Server message: ${JSON.stringify(verifyComponentsExistenceError)}`
      );
    }
  }, [verifyComponentsExistenceStatus]);

  React.useEffect(() => {
    if (saveDraftConfigStatus === QueryStatus.fulfilled) {
      dispatch(
        toastSlice.actions.show({
          title: t('dealerManagement.dealerConfig.saveDraft'),
          message: t('dealerManagement.dealerConfig.saveDraftMessage'),
        })
      );
    }
  }, [saveDraftConfigStatus]);

  React.useEffect(() => {
    if (getConfigResultStatus === QueryStatus.fulfilled) {
      setResultJson(data);
    }
  }, [getConfigResultStatus]);

  // noMissing computed in modal component, not required here

  const handleSubmit = async () => {
    await getConfigResult({
      ticketId: dealerConfig.draftTicketId,
      initialCall: dealerConfig.isInitialCall,
      isTestMode: dealerConfig.isTestMode,
      configJson: dealerConfig.isTestMode
        ? dealerConfig.draftConfiguration
        : dealerConfig.prodConfiguration,
    });
  };

  const handleSaveDraftConfig = async () => {
    await saveDraftConfig({
      draftConfiguration: JSON.parse(dealerConfig.draftConfiguration),
      ticketId: dealerConfig.draftTicketId,
    });
  };

  const validateComponentsExistence = async (
    mandatoryComponentsIds: string[],
    optionalComponentsIds: string[],
    mandatoryThenOptionalComponentsIds: string[]
  ) => {
    await verifyComponentsExistence({
      dealerId: dealerData?.dealerId?.toString() || '',
      mandatoryComponentsIds,
      optionalComponentsIds,
      mandatoryThenOptionalComponentsIds,
    });
  };

  const [
    updateDealerProdConfiguration,
    {
      isError: isSaveToProdDealerError,
      isLoading: isUpdatingProductionConfig,
      status: saveToProdStatus,
    },
  ] = useUpdateDealerProdConfigurationMutation();

  const confirmationToPushConfigToProd = async () => {
    dispatch(confirmationSlice.actions.close());
    // if not in test mode, do not update production config
    if (!dealerConfig.isTestMode) return;
    if (dealerConfig.configurationType !== CONFIGURATION_TYPE.FIXED_JSON) {
      dispatch(
        toastSlice.actions.show({
          title: t('dealerManagement.dealerConfig.saveToProdTitle'),
          message: t('dealerManagement.dealerConfig.configTypeNotSupported'),
        })
      );
      return;
    }
    await updateDealerProdConfiguration({
      configJson: dealerConfig.draftConfiguration,
    });
    // update production config
    setDealerConfig((prev) => ({
      ...prev,
      prodConfiguration: prev.draftConfiguration,
    }));
  };

  const handleUpdateProductionConfig = () => {
    dispatch(
      confirmationSlice.actions.show({
        title: t('dealerManagement.dealerConfig.confirmToProdTitle'),
        message: t('dealerManagement.dealerConfig.confirmToProdMessage'),
        buttonOk: t('dealerManagement.dealerConfig.confirm'),
        buttonCancel: t('component.download.deleteConfirm.buttonCancel'),
      })
    );
  };

  const isApiError =
    isError || isVerifyComponentsExistenceError || isSavingDraftConfigError;
  const apiError =
    error || verifyComponentsExistenceError || saveDraftConfigError;
  let errorMsg = isApiError
    ? (apiError as any)?.data?.message ?? t('unknownError')
    : '';

  if (typeof errorMsg === 'object') {
    errorMsg = JSON.stringify(errorMsg);
  }

  useEffect(() => {
    if (saveToProdStatus === QueryStatus.fulfilled) {
      // show success or error message
      dispatch(
        toastSlice.actions.show({
          title: t('dealerManagement.dealerConfig.saveToProdTitle'),
          message: isSaveToProdDealerError
            ? t('dealerManagement.dealerConfig.saveToProdError')
            : t('dealerManagement.dealerConfig.saveToProdSuccess'),
        })
      );
    }
  }, [saveToProdStatus]);

  return (
    <Row>
      <Col md={12}>
        <Spinner2
          show={
            isLoading ||
            isUpdatingProductionConfig ||
            isVerifyingComponentsExistence ||
            isSavingDraftConfig
          }
        />
        <ConfigComponent
          isError={
            isError || isSaveToProdDealerError || isSavingDraftConfigError
          }
          errorMsg={errorMsg}
          data={resultJson}
          handleSubmit={handleSubmit}
          validateComponentsExistence={validateComponentsExistence}
          handleUpdateProductionConfig={handleUpdateProductionConfig}
          dealerConfig={dealerConfig}
          setDealerConfig={setDealerConfig}
          saveDraftConfig={handleSaveDraftConfig}
        />

        <Confirmation
          key="config-to-prod-confirmation"
          onConfirm={confirmationToPushConfigToProd}
        />
      </Col>
    </Row>
  );
};
